
import React from 'react';
import Box from '../../../components/Common/Box';
import TradingViewChart from '../../../components/Charts/TradingViewChart';
import OrderBookWidget from '../../../components/Widgets/OrderBook/OrderBookWidget';
import CompanyStoryCard from '../../../components/Widgets/CompanyStory/CompanyStoryCard';
import { useScreenType } from '../../../hooks/useScreenType';

interface StockOverviewProps {
    stockDetails: any;
    quotations: any[];
    capitalisation: any;
    chartPeriod: string;
    setChartPeriod: (period: any) => void;
    CHART_PERIOD_OPTIONS: any[];
    latestQuotation: any;
    calculatePerformance: (days: number) => string;
    getWeeklyBreakdown: () => any[];
    formatLargeNumber: (num: number | null) => string;
}

const StockOverview: React.FC<StockOverviewProps> = ({
    stockDetails,
    quotations,
    capitalisation,
    chartPeriod,
    setChartPeriod,
    CHART_PERIOD_OPTIONS,
    latestQuotation,
    calculatePerformance,
    getWeeklyBreakdown,
    formatLargeNumber
}) => {
    const { isMobile } = useScreenType();
    
    // ⚠️ La fenêtre est bornée par la DATE, pas par un nombre de séances.
    // `CHART_PERIOD_OPTIONS.jours` porte des jours calendaires ; les compter comme des
    // lignes de cotation faisait afficher ~7,3 ans pour « 5 Ans », la durée couverte
    // dépendant de la densité de l'historique (2016 n'a que 63 séances en base sur ~250).
    const currentPeriodOption = CHART_PERIOD_OPTIONS.find(opt => opt.value === chartPeriod);
    const jours = currentPeriodOption ? currentPeriodOption.jours : null;

    const borne = new Date();
    if (jours) borne.setDate(borne.getDate() - jours);
    const borneStr = borne.toISOString().slice(0, 10);

    // Repli sur l'historique complet si la fenêtre est vide : jamais de graphe blanc.
    const parDate = jours
        ? quotations.filter(q => (q.date_cotation || q.date_seance || '') >= borneStr)
        : quotations;
    const recentQuotations = parDate.length > 0 ? parDate : quotations;

    const chartData = recentQuotations
        .map((q) => {
            const dateStr = q.date_cotation || q.date_seance;
            if (!dateStr) return null;
            const dateObj = new Date(dateStr);
            if (isNaN(dateObj.getTime())) return null;
            const formattedDate = dateObj.toISOString().split('T')[0];
            if (q.cours_cloture === null || q.cours_cloture === undefined) return null;
            return { time: formattedDate, value: q.cours_cloture };
        })
        .filter((item): item is { time: string; value: number } => item !== null)
        .reverse(); // Convert to Oldest First for the chart

    const weeklyBreakdown = getWeeklyBreakdown();

    return (
        <div className="stock-overview-container">
            {/* 3. Consolidated Market Profile (Identity + Capital) */}
            <Box style={{ marginBottom: '20px' }}>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Profil Marché</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: isMobile ? '15px' : '25px', display: 'flex', gap: isMobile ? '20px' : '40px', flexWrap: 'wrap' }}>

                    {/* LEFT: Identity */}
                    <div style={{ flex: '1.2', display: 'flex', gap: isMobile ? '15px' : '20px', alignItems: 'flex-start', minWidth: isMobile ? '100%' : 'auto' }}>
                        {/* Logo */}
                        <div style={{
                            width: isMobile ? '50px' : '70px',
                            height: isMobile ? '50px' : '70px',
                            borderRadius: '12px',
                            background: 'var(--bg-box)',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                            border: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0
                        }}>
                            <img
                                src={`/images/societes/${stockDetails.symbole}.png`}
                                alt={`Logo de ${stockDetails.nom_societe}`}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (!target.src.includes('error')) {
                                        target.style.display = 'none';
                                        target.parentElement!.innerHTML = `<div style="font-weight:bold; color:#667eea; font-size:12px;">${stockDetails.symbole.substring(0, 4)}</div>`;
                                    }
                                }}
                                style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                            />
                        </div>

                        {/* Details */}
                        <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: '8px', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold', color: 'var(--text-heading)', lineHeight: '1.2' }}>
                                {stockDetails.nom_societe}
                            </div>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '1fr 1fr', 
                                gap: isMobile ? '8px 12px' : '8px 20px', 
                                fontSize: isMobile ? '11px' : '13px', 
                                lineHeight: '1.6' 
                            }}>
                                <div><span style={{ color: '#64748b' }}>Secteur:</span> <b style={{ color: 'var(--text-heading)' }}>{stockDetails.secteur || '-'}</b></div>
                                <div><span style={{ color: '#64748b' }}>Comp.:</span> <b style={{ color: 'var(--text-heading)' }}>{stockDetails.compartiment || '-'}</b></div>
                                <div><span style={{ color: '#64748b' }}>{(latestQuotation as any)?.valeur_echange ? 'Montant échangé :' : 'Volume :'}</span> <b style={{ color: 'var(--text-heading)' }}>{(latestQuotation as any)?.valeur_echange ? `${Math.round((latestQuotation as any).valeur_echange).toLocaleString('fr-FR')} FCFA` : latestQuotation?.volume?.toLocaleString('fr-FR') || 'N/A'}</b></div>
                                <div><span style={{ color: '#64748b' }}>Plus bas – plus haut (clôtures) :</span>
                                    {quotations.length > 0 ? (
                                        <b style={{ marginLeft: '4px' }}>
                                            <span style={{ color: '#ef4444' }}>{Math.min(...quotations.filter(q => q.cours_cloture).map(q => q.cours_cloture)).toLocaleString()}</span>
                                            {' - '}
                                            <span style={{ color: '#22c55e' }}>{Math.max(...quotations.filter(q => q.cours_cloture).map(q => q.cours_cloture)).toLocaleString()}</span>
                                        </b>
                                    ) : 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Divider (Hidden on mobile) */}
                    <div style={{ width: '1px', background: '#e5e7eb', display: 'none' }} className="desktop-divider"></div>
                    <style>{`@media (min-width: 768px) { .desktop-divider { display: block !important; } }`}</style>

                    {/* RIGHT: Capital Structure */}
                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: isMobile ? '100%' : 'auto' }}>
                        {capitalisation ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '12px' : '15px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#64748b', marginBottom: '2px', letterSpacing: '0.5px' }}>Capitalisation</div>
                                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                                        {formatLargeNumber(capitalisation.capitalisation_boursiere || (capitalisation.quantite_totale_titres && latestQuotation?.cours_cloture ? capitalisation.quantite_totale_titres * latestQuotation.cours_cloture : null))}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#64748b', marginBottom: '2px', letterSpacing: '0.5px' }}>Flottant Valo.</div>
                                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                                        {formatLargeNumber(capitalisation.capitalisation_flottante || (capitalisation.quantite_titres_flottant && latestQuotation?.cours_cloture ? capitalisation.quantite_titres_flottant * latestQuotation.cours_cloture : null))}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#64748b', marginBottom: '2px', letterSpacing: '0.5px' }}>Flottant %</div>
                                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: (capitalisation.flottant_pct || 0) >= 15 ? '#16a34a' : '#dc2626' }}>
                                        {capitalisation.flottant_pct?.toFixed(1) || '0'}%
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#64748b', marginBottom: '2px', letterSpacing: '0.5px' }}>Titres Totaux</div>
                                    <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                                        {capitalisation.quantite_totale_titres?.toLocaleString('fr-FR') || '-'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>Données capitalisation indisponibles</p>
                        )}
                    </div>
                </div>
            </Box>

            {/* Fiche Pédagogique — L'Histoire en 2 minutes (Two-Minute Drill de Peter Lynch) */}
            <CompanyStoryCard
                symbole={stockDetails?.symbole}
                nomSociete={stockDetails?.nom_societe}
                secteur={stockDetails?.secteur}
            />

            {/* 1. Performance Indicators Grid */}
            <div className='stock-indicators-grid' style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                gap: isMobile ? '12px' : '16px', 
                marginBottom: '20px' 
            }}>
                {[
                    {
                        label: "Aujourd'hui",
                        value: latestQuotation?.variation_jour_pct?.toFixed(2) + '%' || 'N/A',
                    },
                    { label: 'Cette semaine', value: calculatePerformance(5) },
                    { label: 'Ce mois', value: calculatePerformance(20) },
                    { label: 'Cette année', value: calculatePerformance(Math.min(30, quotations.length)) }, // Approximate YTD/Month logic from previous code
                ].map((indicator, index) => (
                    <Box key={index}>
                        <div style={{ padding: isMobile ? '12px' : '15px', textAlign: 'center' }}>
                            <div style={{ fontSize: isMobile ? '10px' : '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                {indicator.label}
                            </div>
                            <div
                                style={{
                                    fontSize: isMobile ? '16px' : '20px',
                                    fontWeight: 'bold',
                                    color: indicator.value.includes('+')
                                        ? '#4ade80'
                                        : indicator.value.includes('-')
                                            ? '#f87171'
                                            : 'var(--text-heading)',
                                }}
                            >
                                {indicator.value}
                            </div>
                        </div>
                    </Box>
                ))}
            </div>

            {/* 2. Main Chart */}
            <Box>
                <div className='stock-chart-container' style={{ padding: isMobile ? '12px' : '20px' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px' }}>Prix historiques</h3>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {CHART_PERIOD_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => setChartPeriod(option.value)}
                                    style={{
                                        padding: isMobile ? '4px 10px' : '6px 12px',
                                        border: chartPeriod === option.value ? '2px solid #667eea' : '1px solid var(--border-color)',
                                        borderRadius: '20px',
                                        background: chartPeriod === option.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'var(--bg-box)',
                                        color: chartPeriod === option.value ? 'white' : 'var(--text-main)',
                                        cursor: 'pointer',
                                        fontWeight: chartPeriod === option.value ? '600' : '500',
                                        fontSize: isMobile ? '11px' : '12px',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Period Performance Display */}
                    {chartData.length > 1 && (() => {
                        const firstPrice = chartData[0]?.value || 0;
                        const lastPrice = chartData[chartData.length - 1]?.value || 0;
                        const priceChange = lastPrice - firstPrice;
                        const percentChange = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;
                        const isPositive = priceChange >= 0;

                        return (
                            <div style={{ textAlign: isMobile ? 'left' : 'right', marginBottom: '12px', padding: '0' }}>
                                <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: isPositive ? '#22c55e' : '#ef4444' }}>
                                    {isPositive ? '↑' : '↓'} {Math.abs(priceChange).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA ({isPositive ? '+' : ''}{percentChange.toFixed(2)}%)
                                </span>
                            </div>
                        );
                    })()}

                    <div style={{ height: isMobile ? '280px' : '320px', background: 'var(--bg-box)', borderRadius: '8px', padding: '10px', border: '1px solid var(--border-color)' }}>
                        {chartData.length > 0 ? (
                            <TradingViewChart
                                data={chartData}
                                height={isMobile ? 260 : 300}
                                colors={{
                                    lineColor: latestQuotation?.variation_jour_pct >= 0 ? '#22c55e' : '#ef4444',
                                    areaTopColor: latestQuotation?.variation_jour_pct >= 0 ? 'rgba(34, 197, 94, 0.56)' : 'rgba(239, 68, 68, 0.56)',
                                    areaBottomColor: latestQuotation?.variation_jour_pct >= 0 ? 'rgba(34, 197, 94, 0.0)' : 'rgba(239, 68, 68, 0.0)',
                                }}
                            />
                        ) : (
                            <p style={{ textAlign: 'center', paddingTop: '100px', color: 'var(--text-muted)' }}>Aucune donnée disponible</p>
                        )}
                    </div>
                </div>
            </Box>

            {/* 4. Split Layout: History & Order Book */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))', 
                gap: '20px', 
                marginTop: '20px' 
            }}>

                {/* 4a. Weekly History Table */}
                <Box style={{ height: '100%' }}>
                    <div className='box-title box-vertical-padding box-horizontal-padding'>
                        <p>Historique Récent</p>
                    </div>
                    <div className='box-content' style={{ padding: '0', overflowX: 'auto' }}>
                        <table className='weekly-perf-table' style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-box)' }}>
                                    <th style={{ padding: '12px 15px', textAlign: 'left', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date</th>
                                    <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Prix</th>
                                    <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Var.</th>
                                    <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Vol.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {weeklyBreakdown.map((day, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ padding: '10px 15px' }}>
                                            <div style={{ fontWeight: '600', fontSize: isMobile ? '12px' : '14px', color: 'var(--text-main)' }}>{day.day}</div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{day.date}</div>
                                        </td>
                                        <td style={{ padding: '10px 15px', textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px', color: 'var(--text-heading)' }}>{day.price.toLocaleString()}</td>
                                        <td style={{ padding: '10px 15px', textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px', color: day.change >= 0 ? '#22c55e' : '#ef4444' }}>
                                            {day.change >= 0 ? '+' : ''}{day.change.toFixed(2)}%
                                        </td>
                                        <td style={{ padding: '10px 15px', textAlign: 'right', fontSize: isMobile ? '11px' : '13px', color: 'var(--text-muted)' }}>{(day.volume || 0).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>

                {/* 4b. Order Book Widget */}
                <div>
                    <OrderBookWidget symbole={stockDetails.symbole} title="Quantité résiduelle" />
                </div>
            </div>
        </div>
    );
};

export default StockOverview;


